/**
 * Session-JWT storage + light client-side decoding.
 *
 * The JWT is opaque to the frontend — it is never *verified* here, only
 * decoded to read its claims for display (whose email is signed in) and to
 * pre-empt an obviously expired token before firing a request. The backend
 * remains the sole source of truth for validity (`api/deps.py::current_user`).
 */

import type { Locale } from '../types/domain'

const STORAGE_KEY = 'pivot.session'

export interface SessionClaims {
  sub: string
  email: string
  locale: Locale
  iat: number
  exp: number
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string): void {
  localStorage.setItem(STORAGE_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/** Decode the JWT payload (base64url) without verifying its signature. */
export function decodeToken(token: string): SessionClaims | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    )
    return JSON.parse(json) as SessionClaims
  } catch {
    return null
  }
}

export function isExpired(claims: SessionClaims): boolean {
  return claims.exp * 1000 <= Date.now()
}
